import React, { useEffect, useState, useRef } from 'react';
import './Sidebar.css';
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Globals, LogOut as sharedLogOut } from '../globalfunctions.js';
import roleManager from '../utils/roleManager';
import simpleSessionTimer from '../utils/simpleSessionTimer';
import mfaEnforcer from '../utils/mfaEnforcer';
import { useIsMobile } from '../hooks/mobile';
import { useTicketNotificationCount } from '../hooks/useTicketNotificationCount';
import { isItemActive } from '../utils/mobileNav';

const SIDEBAR_EXPANDED_KEY = 'sidebarExpanded';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // On phones the whole navigation is MobileChrome (rendered by Navbar): a
  // bottom tab bar plus a More sheet. This component is the desktop rail only.
  const isMobile = useIsMobile();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const ticketNotificationCount = useTicketNotificationCount(location.pathname, !isMobile);
  const sidebarRef = useRef(null);

  // Close the flyout submenu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setExpandedIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Expanded/collapsed is click-only and persists for the login session
  // (sessionStorage, cleared on logout) - the sidebar never auto-opens on
  // hover anymore.
  const [isExpanded, setIsExpanded] = useState(() => sessionStorage.getItem(SIDEBAR_EXPANDED_KEY) === 'true');

  useEffect(() => {
    sessionStorage.setItem(SIDEBAR_EXPANDED_KEY, isExpanded ? 'true' : 'false');
  }, [isExpanded]);

  const LogOut = () => {
    // Component-specific cleanup stays here; the actual token/storage
    // clearing + server-side cookie clear + redirect is the shared
    // implementation (globalfunctions.js) so it's not duplicated per file.
    simpleSessionTimer.clearSession();
    mfaEnforcer.clear2FASession();
    sessionStorage.removeItem(SIDEBAR_EXPANDED_KEY);

    Globals.UserName = '';
    Globals.UserType = '';
    Globals.LoginTime = null;
    Globals.FirstName = '';
    Globals.LastName = '';

    sharedLogOut();
  }

  const handleMobileNavClick = (navigateFunc) => {
    setExpandedIndex(null);
    navigateFunc();
  };

  // Items with a submenu open a flyout instead of navigating immediately -
  // the flyout itself offers both "go to the list" and the submenu actions.
  const handleNavItemClick = (item, index) => {
    if (item.submenu && item.submenu.length > 0) {
      setExpandedIndex(expandedIndex === index ? null : index);
      return;
    }
    handleMobileNavClick(() => navigate(item.path));
  };

  // Desktop sidebar toggle - the only way the sidebar opens/closes.
  const toggleSidebar = () => {
    setIsExpanded((prev) => !prev);
  };

  // The section you are in stays lit on its sub pages too (/tickets/TICK-1 lights Tickets).
  const isActive = (item) => isItemActive(item, location.pathname);

  // Get navigation items based on user permissions
  const navigationItems = roleManager.getVisibleNavigationItems();

  // Home isn't permission-gated. It has no submenu, so clicking it navigates
  // straight to the dashboard rather than opening a flyout first.
  const allNavItems = [
    { path: '/home', label: 'Home', icon: 'fas fa-house' },
    ...navigationItems
  ];

  if (isMobile) return null;

  // Simple navigation for basic users
  if (navigationItems.length === 0 || Globals.UserType === "readonly") {
    return (
      <div className='navigation-menu'>
        <ol>
          <li onClick={LogOut}>Log Out</li>
          <li><Link to={"/about"}>About</Link></li>
          <li><Link to={"/home"}>Home</Link></li>
        </ol>
      </div>
    );
  }

  return (
    <>
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`sidebar ${isExpanded ? 'expanded' : ''}`}
      >
        <ul>
          {allNavItems.map((item, index) => {
            const hasSubmenu = item.submenu && item.submenu.length > 0;
            return (
              <li key={index} className={isActive(item) ? 'active' : ''}>
                <button
                  type="button"
                  className="nav-link-btn"
                  aria-haspopup={hasSubmenu ? 'true' : undefined}
                  aria-expanded={hasSubmenu ? expandedIndex === index : undefined}
                  aria-current={isActive(item) ? 'page' : undefined}
                  aria-label={item.label}
                  title={isExpanded ? undefined : item.label}
                  onClick={() => handleNavItemClick(item, index)}
                >
                  <span className="nav-icon-wrap">
                    <i className={item.icon}></i>
                    {item.path === '/tickets' && ticketNotificationCount > 0 && (
                      <span className="nav-notification-dot" title={`${ticketNotificationCount} ticket update${ticketNotificationCount === 1 ? '' : 's'}`}></span>
                    )}
                  </span>
                  <span className="menu-text">
                    {item.label}
                    {item.path === '/tickets' && ticketNotificationCount > 0 && (
                      <span className="nav-notification-count">{ticketNotificationCount}</span>
                    )}
                  </span>
                  {hasSubmenu && <i className="fas fa-chevron-right submenu-caret"></i>}
                </button>

                {hasSubmenu && expandedIndex === index && (
                  <div className="submenu-flyout" onClick={(e) => e.stopPropagation()}>
                    <div className="submenu-flyout-header">{item.label}</div>
                    {item.submenu.map((subItem, subIndex) => (
                      <button
                        key={subIndex}
                        type="button"
                        className="submenu-flyout-item"
                        onClick={() => handleMobileNavClick(() => navigate(subItem.path))}
                      >
                        <i className={subItem.icon}></i>
                        <span>{subItem.label}</span>
                      </button>
                    ))}
                  </div>
                )}

              </li>
            );
          })}
        </ul>

        {/* Bottom toggle - the only control that opens/closes the sidebar */}
        {(
          <button type="button" className="collapse-btn" onClick={toggleSidebar} aria-label={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}>
            <i className={`fas fa-angles-${isExpanded ? 'left' : 'right'}`}></i>
            <span className="menu-text">{isExpanded ? 'Collapse' : 'Expand'}</span>
          </button>
        )}
      </div>
    </>
  );
}

export default Sidebar;
